import { set } from '@ember/object'
import { runTask } from 'ember-lifeline'

type SuccessCallback<Result> = (result: Result) => Promise<void> | void
type ErrorCallback = (error: unknown, errors?: unknown[]) => Promise<void> | void

/**
 * @class SubmitFormRunner
 * SubmitFormRunner provides a unified way of submitting a form, it has 4 public methods which can be chained.
 *
 * It can be used as follows:
 *
 *  new SubmitFormRunner(this, 'submitState')
 *    .beforeSuccess(funcToRunImmediatelyOnSuccess)
 *    .success(funcToRunOnSuccessAfterWaitDelay)
 *    .fail(funcToRunIfAnythingGoesWrong)
 *    .run(promiseReturningFunc.bind(promiseReturningFuncContext), ...promiseReturningFuncArgs)
 *
 * The value of this.submitState (or whichever property name you use) will be updated as the form submission progresses.
 */
export default class SubmitFormRunner<SuccessType = unknown, C extends object = Record<string, unknown>> {
  private stateContext: C
  private statePropertyName: string
  private successDelay: number
  private beforeSuccessCallback?: SuccessCallback<Awaited<SuccessType>>
  private successCallback?: SuccessCallback<Awaited<SuccessType>>
  private errorCallback?: ErrorCallback
  /**
   * We need the context object of the calling code so we can update a state machine variable on it. This way, the UI
   * can respond to state changes as the form submission succeeds or fails.
   *
   * @param {Object} stateContext an Object with a property representing the current state of the form submission.
   * @param {String} statePropertyName the name of the property representing the current state of the form submission.
   * @param {Number} successDelay Number of milliseconds to wait before calling the success callback.
   */
  constructor(stateContext: C, statePropertyName = 'submitState', successDelay = 1000) {
    this.stateContext = stateContext
    this.statePropertyName = statePropertyName
    this.successDelay = successDelay
  }

  beforeSuccess(cb: SuccessCallback<SuccessType>) {
    this.beforeSuccessCallback = cb
    return this
  }

  success(cb: SuccessCallback<SuccessType>) {
    this.successCallback = cb
    return this
  }

  fail(cb: ErrorCallback) {
    this.errorCallback = cb
    return this
  }

  /**
   * This method runs a given function and deals with its response
   * It modifies the submit state variable as well as calls several
   * callbacks in a specific order to work with a submit button.
   *
   * @param method
   * @param args
   */
  async run<Args extends unknown[]>(
    method: (...args: Args) => SuccessType | Promise<SuccessType>,
    ...args: Args
  ): Promise<void> {
    set(this.stateContext, this.statePropertyName, 'load')
    try {
      const result = await method(...args)
      await this.beforeSuccessCallback?.(result)
      runTask(
        this,
        async () => {
          set(this.stateContext, this.statePropertyName, 'done')
          await this.successCallback?.(result)
        },
        this.successDelay,
      )
    } catch (error) {
      set(this.stateContext, this.statePropertyName, 'error')
      if (
        error &&
        typeof error === 'object' &&
        'errors' in error &&
        Array.isArray(error.errors) &&
        error.errors.length > 0 &&
        error.errors[0] &&
        typeof error.errors[0] === 'object' &&
        'title' in error.errors[0] &&
        'detail' in error.errors[0] &&
        typeof error.errors[0].title === 'string' &&
        typeof error.errors[0].detail === 'string'
      ) {
        await this.errorCallback?.(`${error.errors[0].title}: ${error.errors[0].detail}`, error.errors)
      } else {
        await this.errorCallback?.(error)
      }
      console.log(error) // log the error to the console
    }
  }
}
